// @flow

import _ from 'lodash';
import moment from 'moment';

import App from './App.jsx';

import {T_MAV} from './codtype.js';
import {takeExtension, takeDir, takeFilename, takeBasename, add_end_slash} from './utils.js';
import {takeCod} from './codtype.js';
import {find_friends, get_mav_details} from './types.js';
import {fix_dbf} from './Shp.js';

import type {T_Node} from './types.js';
import type {T_ShpFile} from './Shp.js';
import type {T_ImgFile} from './Img.js';
import type {
  T_Err, T_ReporteEscalasLyr, T_TifPngPairs, T_NextFriends, T_NextMavDetail, T_MavDetailRaw,
  T_NextShpAreas, T_ErrAreasZonas, T_MinMax, T_MinMaxNext, T_PngUnits, T_NextPngUnits, T_NextErrNum,
  T_ErrNum, T_ErrPaths, T_Paths, T_NextPaths, T_UsersMinErr, T_User, T_Next, T_Extent, T_NextExtent,
  T_CollectFolder, T_NextUnitInvErr, T_UnitInv
} from './types.js';



export function fetch_dir(app: App, path: string, next: (result: Array<T_Node> | string) => void): void {
  if (takeExtension(path) === 'dir') {
    const url_path = `?path=${path}&session_id=${app.session()}`;
    
    fetch(`${app.remote}/list-dir/${url_path}`).then(resp => resp.json()).then((response: Array<T_Node> | T_Err) => {
      if (response.error && typeof response.error === 'string') {
        next(response.error);
      }
      else if (response instanceof Array) {
        next(response);
      }
    });
  }
  else {
    next('fetch_dir: no se solicitó un directorio');
  }
}



export function fetch_shp(app: App, path: string, next: (result: T_ShpFile | string) => void): void {
  if (takeExtension(path) === 'shp') {
    const url_path     = `?path=${path}&session_id=${app.session()}`,
          simplify_val = app.state.simplify_keep,
          simplify_opt = simplify_val === 1 || takeCod(path) === T_MAV ? '' : `&simplify=true&keep=${simplify_val}`,
          url          = `${app.remote}/get-shp/${url_path}${simplify_opt}`;
    
    fetch(url).then(resp => resp.json()).then((response) => {
      if (response.error && typeof response.error === 'string') {
        next(response.error);
      }
      else {
        next(fix_dbf(response, app.agrosat_user && app.state.user.role !== 'Gerencia'));
      }
    });
  }
  else {
    next('fetch_shp: no se solicitó un shp');
  }
}



export function fetch_png(app: App, path: string, next: (result: T_ImgFile | string) => void): void {
  if (takeExtension(path) === 'png') {
    const url_path = `?path=${path}&session_id=${app.session()}`,
          url      = `${app.remote}/get-png-map/${url_path}`;
    
    fetch(url).then(resp => resp.json()).then((response) => {
      if (response.error && typeof response.error === 'string') {
        next(response.error);
      }
      else {
        next(response);
      }
    });
  }
  else {
    next('fetch_png: no se solicitó una imagen');
  }
}



export function fetch_escalas_lyr(app: App, path: string, next: (T_ReporteEscalasLyr | string) => void): void {
  if (takeExtension(path) === 'dir') {
    const url_path = `?path=${path}&session_id=${app.session()}`,
          url      = `${app.remote}/list-escalas-lyr-txt/${url_path}`;
    
    fetch(url).then(resp => resp.json()).then((response) => {
      if (response.error && typeof response.error === 'string') {
        next(response.error);
      }
      else {
        next(response);
      }
    });
  }
  else {
    next('fetch_escalas_lyr: no se solicitó un directorio');
  }
}



export type T_NextSize = ([number, number] | string) => void;



export function get_tif_size(app: App, tif_path: string, next: T_NextSize): void {
  if (! tif_path) {
    next('no input tif');
  }
  else {
    const url = `${app.remote}/gdal-info/?session_id=${app.session()}&tif=${tif_path}`;
    
    fetch(url).then(resp => resp.json()).then((response: {size: [number, number]} | T_Err) => {
      if (response.error && typeof response.error === 'string') {
        next(response.error);
      }
      else if (response.size && response.size instanceof Array) {
        next(response.size);
      }
      else {
        next('unknown size');
      }
    });
  }
}



export type T_NextValue = (number | string) => void;



export function get_tif_pixel(app: App, tif_path: string, px: number, py: number, next: T_NextValue): void {
  if (! tif_path) {
    next('no input tif');
  }
  else {
    const url = `${app.remote}/gdal-tif-pixel/?session_id=${app.session()}&tif=${tif_path}&px=${px}&py=${py}`;
    
    fetch(url).then(resp => resp.json()).then((response: number | T_Err) => {
      if (response.error && typeof response.error === 'string') {
        next(response.error);
      }
      else if (typeof response === 'number') {
        next(response);
      }
      else {
        next('Valor de pixel desconocido.');
      }
    });
  }
}



export function get_tif_friend_of(app: App, png_path: string, next: T_NextFriends): void {
  const dir     = takeDir(png_path),
        is_diff = takeFilename(dir) === 'Mapas_Diferenciales';
  
  if (is_diff) {
    const png_file = takeFilename(png_path);
    
    next({
      elem: `MD_${takeBasename(png_file).slice(5)}`,
      tif : `${png_file.slice(0, -4)}.tif`,
      png : png_file
    });
  }
  else {
    const url = `${app.remote}/get-tif-png-pairs/?dir=${dir}&session_id=${app.session()}`;
    
    fetch(url).then(resp => resp.json()).then((fs: T_TifPngPairs) => {
      next(find_friends(fs, takeFilename(png_path)));
    });
  }
}



export function get_mav_detail(app: App, mav_path: string, next: T_NextMavDetail): void {
  const jpath = `${mav_path}.json`,
        url   = `${app.remote}/get-json/?path=${jpath}&session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((res) => {
    if (res.error && typeof res.error === 'string') {
      next(null);
    }
    else {
      const mav_detail_raw = (res: T_MavDetailRaw);
      
      next(get_mav_details(mav_detail_raw, mav_path));
    }
  });
}



export function get_mav_areas(app: App, shp_path: string, next: T_NextShpAreas): void {
  const url = `${app.remote}/gdal-mav-areas/?shp_path=${shp_path}&session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((res_areas: T_ErrAreasZonas) => {
    next(res_areas);
  });
}



export function get_mapatematico_minmax(app: App, tif_path: string, next: T_MinMaxNext): void {
  const json = `${tif_path}.minmax.json`,
        url  = `${app.remote}/get-json/?path=${json}&session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((res: T_Err | T_MinMax) => {
    if (res instanceof Array) {
      next(res);
    }
    else {
      next([0, 0]);
    }
  });
}



export function get_units_of_dir(app: App, dir: string, next: T_NextPngUnits): void {
  const url = `${app.remote}/get-units-of-dir/?dir=${dir}&session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((res_units: T_PngUnits) => {
    next(res_units);
  });
}



export function get_area_extent(app: App, path: string, next: T_NextErrNum): void {
  const ext = path.substr(path.lastIndexOf('.') + 1).toLowerCase();
  
  if (ext === 'shp' || ext === 'tif') {
    const url = `${app.remote}/gdal-${ext}-extent/?path=${path}&session_id=${app.session()}`;
    
    fetch(url).then(resp => resp.json()).then((mb_num: T_ErrNum) => {
      next(mb_num);
    });
  }
  else {
    next({error: 'Tipo de archivo inválido.'});
  }
}





export function get_tif_rect(app: App, path: string, next: T_NextExtent): void {
  if (path.substr(path.lastIndexOf('.') + 1).toLowerCase() === 'tif') {
    const url = `${app.remote}/gdal-tif-extent-rect/?path=${path}&session_id=${app.session()}`;
    
    fetch(url).then(resp => resp.json()).then((mb_ext: string | T_Extent) => {
      next(typeof mb_ext === 'string' ? null : mb_ext);
    });
  }
  else {
    next(null);
  }
}




export function get_pixel_size(app: App, path: string, next: T_NextErrNum): void {
  const ext = path.substr(path.lastIndexOf('.') + 1).toLowerCase();
  
  if (ext === 'tif') {
    const url = `${app.remote}/gdal-tif-pixel-size/?path=${path}&session_id=${app.session()}`;
    
    fetch(url).then(resp => resp.json()).then((mb_num: T_ErrNum) => {
      next(mb_num);
    });
  }
  else {
    next({error: 'Tipo de archivo inválido.'});
  }
}





// NOTE: esta función devuelve una lista de paths de cultivables, no de directorios. 

export function get_all_services(app: App, dir: string, next: (paths: T_Paths) => void): void {
  const url = `${app.remote}/get-all-cultis/?dir=${dir}&session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((mb_paths: T_ErrPaths) => {
    if (mb_paths.error && typeof mb_paths.error === 'string') {
      console.error('get_all_services:', mb_paths.error);
      
      next([]);
    }
    else if (mb_paths instanceof Array) {
      next(mb_paths.filter(path => takeFilename(takeDir(path)).indexOf('_') !== 0));
    }
  });
}



export function fetch_users_min(app: App, next: (response: T_UsersMinErr) => void): void {
  const url = `${app.remote}/get-users-min/?session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((response: T_UsersMinErr) => {
    next(response);
  });
}



export function fetch_user_details(app: App, user_name: string, next: (response: T_User | string) => void): void {
  const url = `${app.remote}/get-user-details/?session_id=${app.session()}&user_name=${user_name}`;
  
  fetch(url).then(resp => resp.json()).then((response) => {
    next(response);
  });
}



export function get_mapatematico_fathers(app: App, mt_path: string, next: T_NextPaths): void {
  const json = `${mt_path}.fathers.json`,
        url  = `${app.remote}/get-json/?path=${json}&session_id=${app.session()}`;
  
  fetch(url).then(resp => resp.json()).then((res: {fathers: T_Paths} | {error: string}) => {
    if (res.error && typeof res.error) {
      console.error(res.error);
      
      next([]);
    }
    else if (res.fathers && (res.fathers instanceof Array)) {
      next(res.fathers);
    }
    else {
      next([]);
    }
  });
}



export function get_ms_of_dir(app: App, dir: string, next: (result: T_ShpFile | string) => void): void {
  const ses = app.session(),
        url = `${app.remote}/get-culti-of-dir/?session_id=${ses}&dir=${dir}&param1=&glob=m[fs]_*.shp`;
  
  fetch(url).then(resp => resp.json()).then((data) => {
    if (! data) {
      next('Error: no pude encontrar un ms.');
    }
    else {
      next(data);
    }
  });
}



export function does_path_exists(app: App, path: string, next: T_Next): void {
  const url = `${app.remote}/does-path-exists/?session_id=${app.session()}&path=${path}`;
  
  fetch(url).then(resp => resp.json()).then((data) => {
    if (data.error) {
      next(false);
    }
    else if (typeof data === 'boolean') {
      next(data);
    }
  });
}



export function get_culti_of_dir(app: App, dir: string, next: (result: T_ShpFile | string) => void): void {
  const ses = app.session(),
        url = `${app.remote}/get-culti-of-dir/?session_id=${ses}&dir=${dir}&param1=&glob=cultivable_*.shp`;
  
  fetch(url).then(resp => resp.json()).then((data) => {
    if (! data) {
      next('Error: no pude encontrar un cultivable.');
    }
    else {
      next(data);
    }
  });
}



export function copy_culti(app: App, culti_path: string, dest_dir: string, next: T_Next): void {
  const culti_file = takeFilename(culti_path),
        culti_dest = add_end_slash(dest_dir) + culti_file;
  
  does_path_exists(app, culti_dest, (culti_exists) => {
    if (culti_exists) {
      next(true);
    }
    else {
      const culti_base = takeBasename(culti_file),
            files      = ['shp', 'shx', 'prj', 'dbf'].map(ext => `${culti_base}.${ext}`);
      
      app.copy_files_from(takeDir(culti_path), files, dest_dir, (response_cp) => {
        next(_.has(response_cp, 'ok'));
      });
    }
  });
}



type T_NextMbCollect = (string | T_CollectFolder) => void;



export function fetch_collects(app: App, dir: string, next: T_NextMbCollect): void {
  const ses = app.session(),
        url = `${app.remote}/collect-points-of-dir/?session_id=${ses}&dir=${dir}`;
  
  fetch(url).then(resp => resp.json()).then((data) => {
    if (! data) {
      next('Error: no pude encontrar puntos collect.');
    }
    if (typeof data === 'string') {
      next(data);
    }
    else {
      for (const [index, point] of data.points.entries()) {
        point.id      = index;
        point.fmoment = moment(point.fecha, 'DD-MM-YYYY hh:mm:ss');
        point.funix   = point.fmoment.unix();
        point.found   = true;
        
        if (! _.has(point, 'manejos')) {
          point.manejos = [];
        }
      }
      
      next(data);
    }
  });
}





export function fetch_unit_inv_lyr(app: App, lyrPath: string, next: T_NextUnitInvErr): void {
  const ses = app.session(),
        url = `${app.remote}/get-unit-inv-lyr/?session_id=${ses}&lyrPath=${lyrPath}`;
  
  fetch(url).then(resp => resp.json()).then((data: T_UnitInv | string) => {
    next(data);
  });
}



// WEBPACK FOOTER //
// ./src/fetch_things.js